import { QueryKeys, useCustomQuery } from './index';
import Config from '../../config';
import fetch from '../../utils/fetchService';
import { handleErrors, parseAsJson } from '../../utils/requestHelpers';
import { ParamValues } from '../types/customReports';


type UniqueCustomReportParamName = string;
type Option = {
  value: string,
  label:string,
}
type CustomReportParamOptions = Option[]
type CustomReportParamsOptions = Record<UniqueCustomReportParamName, CustomReportParamOptions>;

type CustomReportParamRow = Record<string, string>;

type CustomReportParamDefinition = {
  name: string,
  value: string,
  label: string,
  rows: CustomReportParamRow[],
  reportSetId: CustomReportId,
};

type CustomReportId = string;
type ParamNameToUniqueParamNameMapping = Record<string, string>
type ParamToUniqueIdMapping = Record<CustomReportId, ParamNameToUniqueParamNameMapping>

export const useCustomReportParamsOptions = (
  customReportId: string,
  paramToUniqueIdMapping: ParamToUniqueIdMapping,
  paramValues: ParamValues,
  returnDefaultData = false
) => useCustomQuery<CustomReportParamsOptions>(
  [QueryKeys.customReports.Params, customReportId],
  {
    customFetch: async () => {
      // avoid redundant request
      if (!customReportId || returnDefaultData || !Object.keys(paramValues)) {
        return [];
      }

      const params: CustomReportParamDefinition[] = await fetch(
        Config.endpoints.FETCH_CUSTOM_REPORT_SET_PARAMS
          .replace(':reportSetId', customReportId),
        {
          method: 'POST',
          body: JSON.stringify({ paramValues }),
        }
      )
        .then(handleErrors({ errorMessage: 'Fetching Custom Report Params failed' }))
        .then(parseAsJson);

      const mappedParams = params.map(param => {
        const name = paramToUniqueIdMapping
        && paramToUniqueIdMapping[param.reportSetId]
        && paramToUniqueIdMapping[param.reportSetId][param.name]
          ? paramToUniqueIdMapping[param.reportSetId][param.name]
          : param.name;

        return {
          name,
          options: param.rows.map(row => ({
            value: row[param.value],
            label: row[param.label],
          })),
        };
      });

      return mappedParams.reduce((result: any, param) => {
        if (param) {
          result[param.name] = param.options;
        }
        return result;
      }, {});
    },
    defaultData: {},
  });

export type CustomReportInsertQuery = {
  query: string,
};


type UseCustomReportInsertQueryParams = {
  enabled: boolean,
  reportSetId?: string,
}

export const useCustomReportInsertQuery = ({ reportSetId, enabled } : UseCustomReportInsertQueryParams) =>
  useCustomQuery<CustomReportInsertQuery>([QueryKeys.customReports.CustomReportInsertQuery], {
    url: Config.endpoints.FETCH_CUSTOM_REPORT_INSERT_QUERY
      .replace(/:reportSetId/, `${reportSetId}`),
    defaultData: {
      query: '',
    },
    enabled,
  });
